import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, ChevronsUpDown, EyeOff, Pencil, Plus, RefreshCw, Search, ShieldCheck } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { SegmentedToggle } from '@/components/pages/calendar/facet-filter';
import { PaneHeader } from '@/components/settings/pane-header';
import { permissionsSummary } from '@/components/settings/team/permission-matrix';
import { StaffEditorDialog } from '@/components/settings/team/staff-editor-dialog';
import { fetchPermissionGroups, fetchStaff, fetchTeams } from '@/lib/api/team';
import { getAvatarColorClasses } from '@/lib/messages/avatar-color';
import { initialsFromLabel } from '@/lib/messages/messages-helpers';
import { mergePermissionMaps } from '@/lib/permissions';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 50;
const COLUMN_COUNT = 7;

const STATUS_SEGMENTS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const ROLE_SEGMENTS = [
  { id: 'all', label: 'All roles' },
  { id: 'coordinator', label: 'Coordinators' },
  { id: 'doctor', label: 'Doctors' },
];

/**
 * A header cell whose label is a real button (a clickable <th> is not keyboard-operable) and
 * whose `aria-sort` announces the active column. Only the server-sortable columns get one —
 * Access and Teams are client-side enrichments with no ordering behind them.
 */
function SortableTh({ label, sortKey, sort, onSort, className }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        // `uppercase` is repeated here on purpose: preflight resets text-transform on <button>,
        // so the <th>'s uppercase would not reach this label.
        className="group/sort inline-flex items-center gap-1 rounded uppercase transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}
        <Icon
          aria-hidden
          className={cn(
            'h-3.5 w-3.5 transition-opacity',
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-50 group-focus-visible/sort:opacity-50',
          )}
        />
      </button>
    </th>
  );
}

/**
 * Compact filter dropdown for the toolbar. A native select rather than a popover: teams and access
 * groups are open-ended lists, and the OS control already gives keyboard search and scrolling for
 * free. Styling mirrors SelectPreferenceRow so it reads as the same family of control.
 */
function FilterSelect({ value, onChange, options, allLabel, ariaLabel }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={ariaLabel}
      className={cn(
        'h-9 max-w-44 shrink-0 rounded-md border border-input bg-background px-2 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        !value && 'text-muted-foreground',
      )}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
  );
}

/** Two chips plus a "+N" tail — the shared shape of the Access and Teams cells. */
function ChipList({ names, className }) {
  const shown = names.slice(0, 2);
  const overflow = names.length - shown.length;
  return (
    <span className="flex min-w-0 items-center gap-1">
      {shown.map((name) => (
        <Badge key={name} variant="outline" className={cn('max-w-32 truncate whitespace-nowrap text-[10px] font-normal', className)}>
          {name}
        </Badge>
      ))}
      {overflow > 0 ? (
        <span className="shrink-0 text-[11px] text-muted-foreground" title={names.slice(2).join(', ')}>
          +{overflow}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Admins bypass groups entirely, so their access is stated, never computed. Everyone else gets
 * their group chips plus the union of those groups' rights ("4 manage · 3 view") — the same
 * per-module MAX the backend applies.
 */
function AccessCell({ user, groupsById }) {
  if (user.isAdmin) {
    return (
      <Badge
        variant="secondary"
        className="gap-1 whitespace-nowrap border-amber-200 bg-amber-50 text-[10px] text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300"
      >
        <ShieldCheck className="h-3 w-3 shrink-0" aria-hidden /> Full access
      </Badge>
    );
  }
  if (user.permissionGroupIds.length === 0) {
    return <span className="whitespace-nowrap text-xs italic text-muted-foreground">No access groups</span>;
  }

  const groupNames = user.permissionGroupIds.map((id) => groupsById.get(id)?.name).filter(Boolean);
  const summary = permissionsSummary(
    mergePermissionMaps(user.permissionGroupIds.map((id) => groupsById.get(id)?.permissions).filter(Boolean)),
    { includeHidden: false },
  );

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {groupNames.length > 0 ? (
        <ChipList names={groupNames} />
      ) : (
        // The groups fetch failed or raced: never claim "no access groups" when ids exist.
        <span className="text-xs text-muted-foreground">
          {user.permissionGroupIds.length} group{user.permissionGroupIds.length === 1 ? '' : 's'}
        </span>
      )}
      {summary ? <span className="whitespace-nowrap text-[11px] text-muted-foreground">{summary}</span> : null}
    </div>
  );
}

function EmployeeRow({ user, groupsById, teamsById, onEdit }) {
  const name = user.displayName || user.loginId || 'Unnamed';
  const active = user.status !== 'inactive';
  const isDoctor = user.userType === 'doctor';
  // Name falls back to the raw id: unnamed teams (auto-created care teams) and teams beyond
  // the directory's 50-result window must never render as blank chips.
  const teamNames = (user.teamIds || []).map((id) => teamsById.get(id)?.name || id);

  return (
    <tr className="group transition-colors hover:bg-muted/40">
      {/* Left edge markers rather than row tints, so they don't compete with the hover state.
          Doctors are sky, admins amber (matching the Admin badge); an admin doctor gets both bars
          side by side rather than one overriding the other. Decorative — the Role column and the
          Admin badge carry the same information for assistive tech.

          Absolutely positioned on purpose: a border would widen the cell, and since only some rows
          have one the body columns would drift out of line with the sticky header. */}
      <td className="relative px-3 py-2">
        <span className="absolute inset-y-0 left-0 flex" aria-hidden>
          {isDoctor ? <span className="w-1 bg-sky-500" /> : null}
          {user.isAdmin ? <span className="w-1 bg-amber-500" /> : null}
        </span>
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar className="h-8 w-8 shrink-0 ring-1 ring-border/60">
            {/* Radix keeps the fallback until the image loads, so a missing photo never flashes. */}
            {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
            {/* Seeded by id, not name, so the colour survives a rename. */}
            <AvatarFallback className={cn('text-[11px] font-bold', getAvatarColorClasses(user.id || user.loginId))}>
              {initialsFromLabel(name)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate text-[13px] font-medium text-foreground">{name}</span>
          {user.isAdmin ? (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 border-amber-200 bg-amber-50 text-[10px] text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300"
            >
              <ShieldCheck className="h-3 w-3" aria-hidden /> Admin
            </Badge>
          ) : null}
          {/* Only reachable with "Show hidden" on, so the badge explains why the row is here. */}
          {user.hiddenFromStaffList ? (
            <Badge variant="secondary" className="shrink-0 gap-1 text-[10px] text-muted-foreground">
              <EyeOff className="h-3 w-3" aria-hidden /> Hidden
            </Badge>
          ) : null}
        </div>
      </td>
      <td className="max-w-48 truncate px-3 py-2 font-mono text-xs text-muted-foreground">{user.loginId}</td>
      <td className="px-3 py-2">
        <Badge variant="secondary" className="whitespace-nowrap text-[10px] capitalize">
          {user.userType || 'coordinator'}
        </Badge>
      </td>
      <td className="px-3 py-2">
        <AccessCell user={user} groupsById={groupsById} />
      </td>
      <td className="px-3 py-2">
        {teamNames.length > 0 ? (
          <ChipList
            names={teamNames}
            className="border-teal-300/60 text-teal-700 dark:border-teal-500/40 dark:text-teal-300"
          />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {/* Dot plus label: colour alone would be the only carrier of meaning. */}
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
          <span
            aria-hidden
            className={cn('size-1.5 shrink-0 rounded-full', active ? 'bg-emerald-500' : 'bg-muted-foreground/50')}
          />
          <span className={active ? 'text-foreground' : 'text-muted-foreground'}>
            {active ? 'Active' : 'Inactive'}
          </span>
        </span>
      </td>
      <td className="w-12 px-3 py-2 text-right">
        {/* opacity, not `hidden`: the button must stay in the tab order. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Edit ${name}`}
          title={`Edit ${name}`}
          onClick={() => onEdit(user)}
          className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

/**
 * Settings → Team → Employees: the staff directory as a sortable table (server-side sort, search
 * and status filter; skip/limit paging). Reachable only with staff:manage (the sub-rail and
 * settings-page both gate it); within it, admin-level edits stay admin-only server-side.
 */
export function TeamEmployeesSubPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  // '' means "no filter" for both, matching the select's placeholder option.
  const [groupFilter, setGroupFilter] = useState('');
  // Accounts flagged "hide from staff list" are excluded server-side; this toggle brings
  // them back so a hidden account can still be found and un-hidden.
  const [showHidden, setShowHidden] = useState(false);
  const [teamFilter, setTeamFilter] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [editor, setEditor] = useState({ open: false, user: null });
  const seqRef = useRef(0);

  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const teamsById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const load = useCallback(async ({ search: term, status, role, groupId, teamId, includeHidden, sort: order, skip, append } = {}) => {
    // Sorting and filtering fire undebounced, so a slow response for the PREVIOUS sort can land
    // after a newer one. Stamp each request and let only the latest touch state — otherwise a
    // late "load more" would append old-order rows onto a freshly sorted list.
    const seq = ++seqRef.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    try {
      const res = await fetchStaff({
        search: term,
        status: status === 'all' ? '' : status,
        userType: role === 'all' ? '' : role,
        permissionGroupId: groupId,
        teamId,
        includeHidden,
        sort: order.key,
        dir: order.dir,
        skip,
        limit: PAGE_SIZE,
      });
      if (seq !== seqRef.current) return;
      setTotal(res.total);
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
    } catch (error) {
      if (seq !== seqRef.current) return;
      toast.error(`Failed to load employees: ${error.message || error}`);
      if (!append) setItems([]);
    } finally {
      if (seq === seqRef.current) {
        if (append) setIsLoadingMore(false);
        else setIsLoading(false);
      }
    }
  }, []);

  // Only the free-text term is debounced; sort, status and role changes must feel instant.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), search.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search]);

  // Any term/filter/sort change resets to the first page.
  useEffect(() => {
    load({
      search: debouncedSearch,
      status: statusFilter,
      role: roleFilter,
      groupId: groupFilter,
      teamId: teamFilter,
      includeHidden: showHidden,
      sort,
      skip: 0,
      append: false,
    });
  }, [debouncedSearch, statusFilter, roleFilter, groupFilter, teamFilter, showHidden, sort, load]);

  // Group + team names for the row chips + the editor's multi-selects (one fetch each).
  useEffect(() => {
    let cancelled = false;
    fetchPermissionGroups()
      .then((res) => {
        if (!cancelled) setGroups(res.items);
      })
      .catch(() => {
        /* chips fall back to a group count; the editor shows its empty-state hint */
      });
    fetchTeams()
      .then((res) => {
        if (!cancelled) setTeams(res.items);
      })
      .catch(() => {
        /* same graceful fallback as groups */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(() => {
    load({
      search: debouncedSearch,
      status: statusFilter,
      role: roleFilter,
      groupId: groupFilter,
      teamId: teamFilter,
      includeHidden: showHidden,
      sort,
      skip: 0,
      append: false,
    });
  }, [load, debouncedSearch, statusFilter, roleFilter, groupFilter, teamFilter, showHidden, sort]);

  const toggleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }, []);

  const headerProps = { sort, onSort: toggleSort };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Employees"
        description="Staff accounts and their access-group assignment. Admins always have full access."
        action={
          <Button type="button" size="sm" onClick={() => setEditor({ open: true, user: null })}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Add employee
          </Button>
        }
      />

      {/* flex-wrap + a search min-width: this pane is user-resizable, and with two filter groups a
          nowrap row squeezes the search until its placeholder truncates. Filters wrap instead. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
        <div className="relative min-w-56 max-w-64 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or login…"
            className="h-9 pl-8"
          />
        </div>
        <SegmentedToggle
          value={roleFilter}
          segments={ROLE_SEGMENTS}
          onChange={setRoleFilter}
          ariaLabel="Filter by role"
        />
        <SegmentedToggle
          value={statusFilter}
          segments={STATUS_SEGMENTS}
          onChange={setStatusFilter}
          ariaLabel="Filter by account status"
        />
        <FilterSelect
          value={teamFilter}
          onChange={setTeamFilter}
          options={teams}
          allLabel="All teams"
          ariaLabel="Filter by team"
        />
        <FilterSelect
          value={groupFilter}
          onChange={setGroupFilter}
          options={groups}
          allLabel="All access groups"
          ariaLabel="Filter by access group"
        />
        <label
          className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-muted-foreground"
          title="Include accounts flagged “hide from staff list”"
        >
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            className="size-3.5 accent-primary"
          />
          Show hidden
        </label>
        <span className="ml-auto text-xs text-muted-foreground">
          {total} employee{total === 1 ? '' : 's'}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={refresh} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* orientation="both": six columns would crush rather than scroll in a narrow pane. */}
      <ScrollArea orientation="both" className="min-h-0 flex-1">
        {/* border-separate is load-bearing: with the default border-collapse the sticky header's
            bottom border belongs to the shared cell edge and scrolls away with the body. */}
        <table className="w-full min-w-[900px] border-separate border-spacing-0 text-sm">
          <caption className="sr-only">Employees, their access groups, teams and account status</caption>
          <thead className="sticky top-0 z-10">
            <tr className="[&>th]:border-b [&>th]:border-border [&>th]:bg-muted [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:text-[11px] [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-muted-foreground">
              <SortableTh label="Name" sortKey="name" {...headerProps} />
              <SortableTh label="Login" sortKey="login" {...headerProps} />
              <SortableTh label="Role" sortKey="role" {...headerProps} />
              <th scope="col">Access</th>
              <th scope="col">Teams</th>
              <SortableTh label="Status" sortKey="status" {...headerProps} />
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="[&>tr>td]:border-b [&>tr>td]:border-border/60">
            {isLoading ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="p-5">
                  <div className="space-y-2">
                    {[...Array(6)].map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-6 py-10 text-center text-sm text-muted-foreground">
                  {search.trim() || statusFilter !== 'all'
                    ? 'No employees match these filters.'
                    : 'No employees yet.'}
                </td>
              </tr>
            ) : (
              <>
                {items.map((user) => (
                  <EmployeeRow
                    key={user.id}
                    user={user}
                    groupsById={groupsById}
                    teamsById={teamsById}
                    onEdit={(u) => setEditor({ open: true, user: u })}
                  />
                ))}
                {items.length < total ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="p-4 text-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isLoadingMore}
                        onClick={() =>
                          load({
                            search: debouncedSearch,
                            status: statusFilter,
                            role: roleFilter,
                            groupId: groupFilter,
                            teamId: teamFilter,
                            sort,
                            skip: items.length,
                            append: true,
                          })
                        }
                      >
                        {isLoadingMore ? 'Loading…' : `Load more (${total - items.length} left)`}
                      </Button>
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </ScrollArea>

      <StaffEditorDialog
        open={editor.open}
        onOpenChange={(open) => setEditor((prev) => ({ ...prev, open }))}
        user={editor.user}
        groups={groups}
        teams={teams}
        onSaved={refresh}
      />
    </div>
  );
}
